import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  InputAdornment,
  Collapse,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation, Trans } from 'react-i18next';
import { hasuraClient } from '../../utils/graphqlClient';
import {
  GET_FIRMS_BY_NAME_QUERY,
  GET_FIRMS_BY_SHORT_NAME_QUERY,
} from '../../graphql/queries';
import { useAuth } from '../../hooks/useReduxAuth';
import { formFieldStyles } from './FormField.styles';

const FirmSuggestionField = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  const [matches, setMatches] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const debounceRef = useRef(null);
  const companyId = currentUser?.companyId;

  const isFirmName = field.name === 'firm_name';

  const search = useCallback(
    async (inputVal) => {
      const trimmed = (inputVal || '').trim();

      if (trimmed.length === 0) {
        setMatches([]);
        return;
      }

      if (!companyId) return;

      const pattern = `${trimmed}%`;
      const query = isFirmName ? GET_FIRMS_BY_NAME_QUERY : GET_FIRMS_BY_SHORT_NAME_QUERY;
      const resultKey = isFirmName ? 'firms' : 'firms';

      setIsSearching(true);
      try {
        const data = await hasuraClient.query(query, { pattern, companyId });
        setMatches(data?.[resultKey] || []);
      } catch (err) {
        console.error('FirmSuggestionField fetch error:', err);
        setMatches([]);
      } finally {
        setIsSearching(false);
      }
    },
    [companyId, isFirmName]
  );

  const handleChange = (e) => {
    const newVal = e.target.value;
    onChange(field.name, newVal);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(newVal), 350);
  };

  const handleBlur = () => {
    if (onBlur) onBlur(field.name, value);
  };

  const hasMatches = matches.length > 0;

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        fullWidth
        label={field.label ? t(field.label) : ''}
        placeholder={field.placeholder ? t(field.placeholder) : ''}
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        error={!!error}
        helperText={error}
        autoFocus={field.autoFocus}
        sx={formFieldStyles.field}
        InputProps={{
          endAdornment: isSearching ? (
            <InputAdornment position="end">
              <CircularProgress size={16} sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ) : null,
        }}
      />

      <Collapse in={hasMatches} unmountOnExit>
        <Alert
          severity="warning"
          icon={<WarningAmberIcon fontSize="small" />}
          sx={{
            mt: 0.5,
            borderRadius: 1.5,
            py: 0.5,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <AlertTitle sx={{ fontSize: '0.8rem', mb: 0.5 }}>
            <Trans
              i18nKey="firms.form.similarFirmsWarning"
              count={matches.length}
              components={{ 1: <strong /> }}
            />
          </AlertTitle>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {matches.map((firm) => (
              <Chip
                key={firm.id}
                label={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography component="span" variant="caption" fontWeight={700}>
                      {firm.firm_short_name}
                    </Typography>
                    {firm.firm_name && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        — {firm.firm_name}
                      </Typography>
                    )}
                  </Box>
                }
                size="small"
                variant="outlined"
                color="warning"
                sx={{ height: 'auto', py: 0.25 }}
              />
            ))}
          </Box>
        </Alert>
      </Collapse>
    </Box>
  );
};

export default FirmSuggestionField;
